import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/unsubscribe")({
  head: () => ({
    meta: [
      { title: "Unsubscribe · Surer Adda With Sanchayeeta" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: UnsubscribePage,
});

type State = "loading" | "ready" | "already" | "invalid" | "submitting" | "done" | "error";

function UnsubscribePage() {
  const [state, setState] = useState<State>("loading");
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    const t = new URLSearchParams(window.location.search).get("token");
    if (!t) { setState("invalid"); return; }
    setToken(t);
    fetch(`/email/unsubscribe?token=${encodeURIComponent(t)}`)
      .then((r) => r.json())
      .then((d) => {
        if (d?.valid) setState("ready");
        else if (d?.reason === "already_unsubscribed") setState("already");
        else setState("invalid");
      })
      .catch(() => setState("error"));
  }, []);

  const confirm = async () => {
    if (!token) return;
    setState("submitting");
    try {
      const r = await fetch("/email/unsubscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      const d = await r.json();
      if (d?.success) setState("done");
      else if (d?.reason === "already_unsubscribed") setState("already");
      else setState("error");
    } catch {
      setState("error");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <section className="mx-auto max-w-xl px-4 py-20 sm:px-6">
        <div className="rounded-2xl border border-border bg-card p-8 text-center shadow-sm sm:p-12">
          <h1 className="text-3xl">Unsubscribe</h1>
          <div className="mt-4 text-muted-foreground">
            {state === "loading" && <p>Checking your link…</p>}
            {state === "ready" && (
              <>
                <p>Click below to stop receiving emails from Surer Adda With Sanchayeeta.</p>
                <Button onClick={confirm} className="mt-6">Confirm unsubscribe</Button>
              </>
            )}
            {state === "submitting" && <p>Processing…</p>}
            {state === "done" && <p className="text-foreground">You've been unsubscribed. We're sorry to see you go.</p>}
            {state === "already" && <p className="text-foreground">This address is already unsubscribed.</p>}
            {state === "invalid" && <p>This unsubscribe link is invalid or expired.</p>}
            {state === "error" && <p>Something went wrong. Please try again later.</p>}
          </div>
        </div>
      </section>
      <SiteFooter />
    </div>
  );
}
